/**
 * SrTopLevelsDrawer.jsx — Cross-coin Top S/R levels panel (Fase 2, 2026-02).
 *
 * Read-only consumer of GET /api/liqmap/sr-top-levels/list. Reuses the
 * already-validated strength_score / strength_tier pipeline — does NOT
 * compute anything. Mirrors the pattern of CascadeRecentDrawer for layout,
 * styling AND auth (Bearer header via fetch, NO withCredentials — avoids
 * the CORS-with-credentials clash that breaks Network in production where
 * the backend echoes `Access-Control-Allow-Origin: *`).
 */
import { useEffect, useState, useCallback } from "react";
import { tLm } from "../../i18n/liqmap.js";
import { useHardcodedI18n } from "../../i18n/hardcoded";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIMEFRAMES = ["15m", "1h", "4h", "1d"];
const LIMITS = [5, 10, 20];
const COINS_FILTER = [null, "BTC", "ETH", "SOL", "BNB", "XRP", "ZEC"];
// When a specific coin is selected we need ALL its levels (not just those
// that landed in the global top N). Each coin has at most 6 levels per
// timeframe (3 R + 3 S) ⇒ requesting limit=50 from backend is safe and
// guarantees we receive every level for any single coin.
const COIN_FILTER_FETCH_LIMIT = 50;
const TIER_STYLE = {
  fuerte: {
    R: { bg: "rgba(255,59,92,0.16)", border: "#ff3b5c", text: "#ff3b5c" },
    S: { bg: "rgba(0,208,132,0.18)", border: "#00d084", text: "#00d084" },
  },
  media: {
    R: { bg: "rgba(255,140,66,0.12)", border: "#ff8c42", text: "#ff8c42" },
    S: { bg: "rgba(74,222,128,0.12)", border: "#4ade80", text: "#4ade80" },
  },
  debil: {
    R: { bg: "rgba(255,179,128,0.07)", border: "#ffb380", text: "#ffb380" },
    S: { bg: "rgba(134,239,172,0.07)", border: "#86efac", text: "#86efac" },
  },
};

const fmtPrice = (p) => {
  if (!Number.isFinite(p)) return "—";
  if (p < 1) return p.toFixed(4);
  if (p < 100) return p.toFixed(3);
  if (p < 10000) return p.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return p.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

export default function SrTopLevelsDrawer({ open, onClose, token, lang = "es" }) {
  const hc = useHardcodedI18n(lang);
  const t = tLm(lang).sr_top || {};
  const [tf, setTf] = useState("1h");
  const [limit, setLimit] = useState(5);
  const [coin, setCoin] = useState(null); // null = TODAS
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      // Use fetch + Bearer header (NOT axios + withCredentials) to match
      // the pattern of CascadeRecentDrawer. Avoids the CORS-with-credentials
      // clash that breaks production where backend echoes ACA-Origin: '*'.
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // When a specific coin is selected, request a larger window from
      // backend so that ALL the coin's levels are present in the response
      // (the cross-coin top N could otherwise miss some of them). Then we
      // filter + re-truncate to `limit` in-memory below.
      const fetchLimit = coin ? COIN_FILTER_FETCH_LIMIT : limit;
      const url = `${API}/liqmap/sr-top-levels/list?timeframe=${encodeURIComponent(tf)}&limit=${encodeURIComponent(fetchLimit)}`;
      const res = await fetch(url, { headers });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json?.detail || `HTTP ${res.status}`);
      }
      setData(json);
    } catch (e) {
      setErr(e?.message || "err");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [tf, limit, coin, token]);

  // When the drawer opens, reset filters to defaults (TODAS / 1h / top 5).
  // Without this, the local state persists across close/open cycles because
  // the parent keeps the component mounted and just toggles `open`.
  // The state changes trigger `fetchData` automatically via the dependency
  // chain below — no need to call it explicitly here.
  useEffect(() => {
    if (open) {
      setCoin(null);
      setTf("1h");
      setLimit(5);
    }
  }, [open]);

  useEffect(() => {
    if (open) fetchData();
  }, [open, fetchData]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end justify-end sm:items-center sm:justify-center"
      data-testid="sr-top-levels-drawer"
    >
      {/* Backdrop */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Cerrar"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        data-testid="sr-top-levels-backdrop"
      />
      {/* Panel */}
      <div
        className="relative z-10 flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-2xl border bg-[#080510] shadow-2xl sm:rounded-2xl"
        style={{ borderColor: "rgba(249,115,22,0.35)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <div>
            <h2 className="font-mono text-[13px] font-bold text-[#f97316]" data-testid="sr-top-levels-title">
              {t.panel_title || "Top niveles S/R"}
            </h2>
            <p className="mt-0.5 text-[10.5px] text-white/55">{t.panel_subtitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1.5 text-white/60 transition-colors hover:bg-white/5 hover:text-white"
            data-testid="sr-top-levels-close"
            aria-label="Cerrar panel"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3 border-b border-white/10 bg-white/[0.02] px-5 py-3">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[9.5px] uppercase tracking-wider text-white/40">
              {t.tf_label}
            </span>
            <div className="flex gap-1">
              {TIMEFRAMES.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setTf(opt)}
                  data-testid={`sr-top-levels-tf-${opt}`}
                  className={`rounded-md px-2 py-0.5 font-mono text-[10px] font-bold transition-all ${
                    tf === opt
                      ? "bg-[#f97316] text-black"
                      : "bg-white/5 text-white/55 hover:bg-white/10"
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[9.5px] uppercase tracking-wider text-white/40">
              {t.limit_label}
            </span>
            <div className="flex gap-1">
              {LIMITS.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setLimit(n)}
                  data-testid={`sr-top-levels-limit-${n}`}
                  className={`rounded-md px-2 py-0.5 font-mono text-[10px] font-bold transition-all ${
                    limit === n
                      ? "bg-[#f97316] text-black"
                      : "bg-white/5 text-white/55 hover:bg-white/10"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
          {data?.total_evaluated !== undefined && (
            <div className="ml-auto font-mono text-[9.5px] text-white/40" data-testid="sr-top-levels-evaluated">
              {data.total_evaluated} {hc.sr_levels_evaluated} ·{" "}
              <span className="text-white/55">
                {data.coins_succeeded?.length || 0}/{(data.coins_succeeded?.length || 0) + (data.coins_failed?.length || 0)} {hc.sr_coins_label}
              </span>
            </div>
          )}
        </div>

        {/* Coin filter chips (mirrors CascadeRecentDrawer pattern) */}
        <div
          className="flex flex-wrap gap-1.5 border-b border-white/10 bg-white/[0.015] px-5 py-2.5"
          data-testid="sr-top-levels-coin-filter"
        >
          <span className="self-center font-mono text-[9.5px] uppercase tracking-wider text-white/40">
            {t.coin_filter_label}
          </span>
          {COINS_FILTER.map((c) => {
            const active = coin === c;
            const label = c || (t.coin_all || "TODAS");
            return (
              <button
                key={c || "all"}
                type="button"
                onClick={() => setCoin(c)}
                data-testid={`sr-top-levels-coin-${c || "all"}`}
                className="rounded-md px-2.5 py-1 font-mono text-[9px] font-bold transition-all"
                style={
                  active
                    ? {
                        background: "rgba(249,115,22,0.18)",
                        border: "1px solid rgba(249,115,22,0.6)",
                        color: "#f97316",
                      }
                    : {
                        background: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(255,255,255,0.1)",
                        color: "rgba(255,255,255,0.55)",
                      }
                }
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <div className="py-10 text-center font-mono text-[11px] text-white/45" data-testid="sr-top-levels-loading">
              {t.loading}
            </div>
          )}
          {err && !loading && (
            <div className="py-10 text-center font-mono text-[11px] text-rose-400" data-testid="sr-top-levels-error">
              {t.err} <span className="text-white/40">({String(err)})</span>
            </div>
          )}
          {!loading && !err && data?.items?.length === 0 && (
            <div className="py-10 text-center font-mono text-[11px] text-white/45" data-testid="sr-top-levels-empty">
              {t.empty}
            </div>
          )}
          {!loading && !err && data?.items?.length > 0 && (() => {
            // Apply coin filter + re-truncate to `limit` so the user always
            // sees Top-N of the filtered subset (not the global top with gaps).
            const visibleItems = coin
              ? data.items.filter((lv) => lv.coin === coin).slice(0, limit)
              : data.items;
            if (visibleItems.length === 0) {
              return (
                <div className="py-10 text-center font-mono text-[11px] text-white/45" data-testid="sr-top-levels-empty-coin">
                  {(t.empty_coin || "Sin niveles para {coin}.").replace("{coin}", coin || "")}
                </div>
              );
            }
            return (
            <table className="w-full border-collapse" data-testid="sr-top-levels-table">
              <thead>
                <tr className="font-mono text-[9px] uppercase tracking-wider text-white/35">
                  <th className="px-2 py-1.5 text-left">{t.col_rank}</th>
                  <th className="px-2 py-1.5 text-left">{t.col_coin}</th>
                  <th className="px-2 py-1.5 text-left">{t.col_kind}</th>
                  <th className="px-2 py-1.5 text-right">{t.col_price}</th>
                  <th className="px-2 py-1.5 text-right">{t.col_score}</th>
                  <th className="px-2 py-1.5 text-right">{t.col_tasa}</th>
                  <th className="px-2 py-1.5 text-right">{t.col_toques}</th>
                  <th className="px-2 py-1.5 text-right">{t.col_dist}</th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((lv, idx) => {
                  const tier = (lv.strength_tier || "media").toLowerCase();
                  const kind = lv.kind || "R";
                  const style = (TIER_STYLE[tier] && TIER_STYLE[tier][kind]) || TIER_STYLE.media.S;
                  const tierLabel =
                    tier === "fuerte" ? t.tier_fuerte : tier === "media" ? t.tier_media : t.tier_debil;
                  const tasaTxt = lv.tasa_acierto_pct == null ? "—" : `${lv.tasa_acierto_pct}%`;
                  // Build a multi-line breakdown explaining how `strength_score`
                  // is composed (2026-06-27 calibration). The recency line
                  // exposes the new sqrt-shape multiplier so the user can
                  // verify why a level with a recent touch but poor history
                  // doesn't get rewarded as much as before.
                  const sc = lv.strength_components || {};
                  const tierRange = tier === "fuerte" ? "≥75" : tier === "media" ? "50-74" : "<50";
                  const fmt = (n) => (n == null ? "—" : Number(n).toFixed(1));
                  const ageTxt = sc.last_touch_age_candles == null
                    ? "—"
                    : `${sc.last_touch_age_candles}v`;
                  const recencyLine = sc.recency_multiplier != null
                    ? `  recency: ${fmt(sc.recency_norm)} × ${fmt(sc.recency_multiplier * 100)/100} = ${fmt(sc.recency_norm_adj)}  (último toque hace ${ageTxt})`
                    : `  recency: ${fmt(sc.recency_norm)}  (último toque hace ${ageTxt})`;
                  const scoreTooltip = [
                    `Tier: ${tierLabel} (${tierRange})`,
                    `Score: ${Math.round(Number(lv.strength_score) || 0)} / 100`,
                    `Desglose:`,
                    `  success: ${sc.success == null ? "—" : sc.success + "%"}  (peso 40%)`,
                    `  touches: ${fmt(sc.touches_norm)}  (peso 25%)`,
                    recencyLine + `  (peso 20%)`,
                    `  proximity: ${fmt(sc.proximity_norm)}  (peso 15%)`,
                  ].join("\n");
                  return (
                    <tr
                      key={`${lv.coin}-${lv.kind}-${lv.precio}-${idx}`}
                      className="border-t border-white/5 transition-colors hover:bg-white/[0.025]"
                      data-testid={`sr-top-levels-row-${idx + 1}`}
                    >
                      <td className="px-2 py-2 font-mono text-[11px] font-bold text-white/35">
                        {idx + 1}
                      </td>
                      <td className="px-2 py-2 font-mono text-[11px] font-bold text-white">
                        {lv.coin}
                      </td>
                      <td className="px-2 py-2">
                        <span
                          className="inline-flex items-center gap-1 rounded px-1.5 py-[1px] font-mono text-[9.5px] font-bold"
                          style={{
                            background: style.bg,
                            border: `1px solid ${style.border}`,
                            color: style.text,
                          }}
                        >
                          {kind === "R" ? "↑" : "↓"} {kind === "R" ? t.kind_R : t.kind_S}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-[11px] text-white/90">
                        ${fmtPrice(lv.precio)}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          <span
                            className="rounded px-1.5 py-[2px] font-mono text-[10px] font-bold"
                            style={{
                              background: style.bg,
                              color: style.text,
                              border: `1px solid ${style.border}`,
                            }}
                            data-testid={`sr-top-levels-tier-${idx + 1}`}
                            title={scoreTooltip}
                          >
                            ⬢ {Math.round(Number(lv.strength_score) || 0)}
                          </span>
                          <span
                            className="font-mono text-[8.5px] uppercase tracking-wider"
                            style={{ color: style.text, opacity: 0.65 }}
                          >
                            {tierLabel}
                          </span>
                        </div>
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-[10px] text-white/75">
                        {tasaTxt}
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-[10px] text-white/55">
                        {lv.num_toques || 0}
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-[10px] text-white/55">
                        {lv.distancia_pct != null ? `${lv.distancia_pct}%` : "—"}
                        {lv.confirmado_por_liquidaciones && (
                          <span className="ml-1" title={hc.confirmed_by_liq}>🔒</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            );
          })()}
        </div>

        {/* Footer */}
        <div className="border-t border-white/10 bg-white/[0.02] px-5 py-2.5">
          <p className="text-[9.5px] leading-snug text-white/35" data-testid="sr-top-levels-footer">
            {t.footer_hint}
          </p>
        </div>
      </div>
    </div>
  );
}
